import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
  Box,
  IconButton,
  Chip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  DISPUTE_CATEGORY_OPTIONS,
  DISPUTE_CONTEXT_OPTIONS
} from '../../constants/disputeConstants';
import disputeAdminService from '../../services/disputeAdminService';

const getReservationReference = (reservation = {}) => {
  return reservation.reference || reservation.code || reservation.id;
};

const buildDefaultDescription = (reservation, actorRole, t) => {
  const listingName = reservation?.listing?.title || reservation?.listing?.name;
  if (!listingName) {
    return '';
  }
  const base = actorRole === 'owner'
    ? t('defaultReasonOwner', 'Je souhaite ouvrir un litige concernant la reservation {{reference}} pour {{listing}}.', {
        reference: getReservationReference(reservation),
        listing: listingName
      })
    : t('defaultReasonRenter', 'Je souhaite ouvrir un litige concernant la reservation {{reference}} pour {{listing}}.', {
        reference: getReservationReference(reservation),
        listing: listingName
      });
  return base;
};

const DisputeCreationModal = ({
  open,
  onClose,
  reservation,
  actorRole = 'renter',
  onCreated
}) => {
  const { t } = useTranslation('disputes');
  const [category, setCategory] = useState(DISPUTE_CATEGORY_OPTIONS[0].value);
  const [context, setContext] = useState('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [autoHoldDeposit, setAutoHoldDeposit] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingDispute, setExistingDispute] = useState(null);
  const [checkingDispute, setCheckingDispute] = useState(false);

  const reference = useMemo(() => getReservationReference(reservation), [reservation]);

  // 🆕 Vérifier s'il existe déjà un litige pour cette réservation au moment de l'ouverture du modal
  useEffect(() => {
    if (!open || !reservation?.id) {
      setExistingDispute(null);
      return;
    }

    const checkExistingDispute = async () => {
      setCheckingDispute(true);
      try {
        const response = await disputeAdminService.list({
          reservation_id: reservation.id,
          limit: 1
        });

        // Extraire les disputes de la réponse (format peut varier)
        let disputes = [];
        if (Array.isArray(response)) {
          disputes = response;
        } else if (Array.isArray(response?.data)) {
          disputes = response.data;
        } else if (Array.isArray(response?.results)) {
          disputes = response.results;
        }

        if (disputes.length > 0) {
          setExistingDispute(disputes[0]);
        } else {
          setExistingDispute(null);
        }
      } catch (err) {
        console.error('Failed to check existing dispute:', err);
        // Si la requête échoue, on laisse continuer (fail-safe)
        setExistingDispute(null);
      } finally {
        setCheckingDispute(false);
      }
    };

    checkExistingDispute();
  }, [open, reservation?.id]);

  const helperText = useMemo(() => {
    if (actorRole === 'owner') {
      return t('helper.owner', 'Ouvrez une réclamation pour résoudre un problème avec le locataire.');
    }
    return t('helper.renter', 'Ouvrez une réclamation pour résoudre un problème avec le propriétaire.');
  }, [actorRole, t]);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const imageFiles = selectedFiles.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== selectedFiles.length) {
      toast.warning(t('validation.onlyImages', 'Seules les images sont acceptées.'));
    }
    
    const newFiles = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setError('');
    setTitle('');
    setDescription('');
    setCategory(DISPUTE_CATEGORY_OPTIONS[0].value);
    setContext('general');
    setAutoHoldDeposit(false);
    files.forEach(f => URL.revokeObjectURL(f.preview));
    setFiles([]);
    onClose?.();
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    
    if (trimmedTitle.length < 5) {
      setError(t('validation.title', 'Le titre est requis (5 caractères minimum).'));
      return;
    }
    
    if (trimmedDescription.length < 10) {
      setError(t('validation.disputeDescription'));
      return;
    }

    if (!reservation?.id) {
      setError(t('validation.reservationMissing', 'Réservation manquante.'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      
      // Add dispute data
      formData.append('reservation_id', reservation.id);
      formData.append('title', trimmedTitle);
      formData.append('description', trimmedDescription);
      formData.append('category_key', `disputes.categories.${category}`);
      formData.append('context', context);
      formData.append('escalation_level', 'user'); // Toujours 'user' à la création - escalade ultérieure si échec négociation
      formData.append('translation_key', 'disputes.events.created');
      formData.append('translation_params', JSON.stringify({
        actor_role: actorRole,
        reservation_reference: reference
      }));
      formData.append('metadata', JSON.stringify({
        actor_role: actorRole,
        listing_title: reservation?.listing?.title,
        renter_id: reservation?.renter?.id || reservation?.renter_id,
        owner_id: reservation?.listing?.owner?.id || reservation?.listing?.owner_id,
        reservation_reference: reference
      }));

      if (actorRole === 'owner') {
        formData.append('auto_hold_deposit', Boolean(autoHoldDeposit));
      }

      // Add files if any
      files.forEach((fileObj) => {
        formData.append('attachments', fileObj.file);
      });

      const response = await disputeAdminService.create(formData);
      toast.success(t('success.created', 'Litige ouvert, notre équipe vous recontacte rapidement.'));
      onCreated?.(response?.data);
      handleClose();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || t('errors.generic', 'Impossible de créer le litige.');
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const defaultDescription = useMemo(() => buildDefaultDescription(reservation, actorRole, t), [reservation, actorRole, t]);

  if (!reservation) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GavelIcon fontSize="small" />
        {t('modal.title', 'Ouvrir une dispute')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {checkingDispute && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          )}

          {existingDispute && !checkingDispute && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                ⚠️ {t('validation.duplicateDispute', 'Un litige existe déjà pour cette réservation')}
              </Typography>
              <Typography variant="body2">
                {t('validation.duplicateDisputeDesc', 'Une réservation ne peut avoir qu\'un seul litige. Consultez le litige existant pour continuer les négociations.')}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => window.open(`/disputes/${existingDispute.dispute_id || existingDispute.id}`, '_blank')}
                >
                  {t('actions.viewExisting', 'Consulter le litige existant')}
                </Button>
              </Box>
            </Alert>
          )}

          <Alert severity="info">
            <Typography variant="body2">{helperText}</Typography>
          </Alert>

          {/* Explication du processus */}
          <Alert severity="warning" icon={false} sx={{ bgcolor: 'warning.lighter' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📋 {t('process.title', 'Comment ça fonctionne ?')}
            </Typography>
            <Typography variant="caption" component="div">
              1️⃣ {t('process.step1', 'Vous ouvrez une réclamation')}<br/>
              2️⃣ {t('process.step2', 'Négociez directement avec l\'autre partie (propositions/contre-propositions)')}<br/>
              3️⃣ {t('process.step3', 'Si accord → résolution automatique')}<br/>
              4️⃣ {t('process.step4', 'Si désaccord → vous pouvez escalader au support Krillou')}
            </Typography>
          </Alert>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {t('labels.reservation', 'Réservation')}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              #{reference}
            </Typography>
            {reservation?.listing?.title && (
              <Typography variant="body2" color="text.secondary">
                {reservation.listing.title}
              </Typography>
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel>{t('labels.category', 'Motif principal')}</InputLabel>
            <Select
              label={t('labels.category', 'Motif principal')}
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {DISPUTE_CATEGORY_OPTIONS.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {t(option.translationKey, option.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('labels.context', 'Contexte')}</InputLabel>
            <Select
              label={t('labels.context', 'Contexte')}
              value={context}
              onChange={(event) => setContext(event.target.value)}
            >
              {DISPUTE_CONTEXT_OPTIONS.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {t(option.translationKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('labels.title')}
            placeholder={t('placeholders.title', 'Ex: Dommages constatés sur le véhicule')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
            required
          />

          <TextField
            label={t('labels.description')}
            multiline
            minRows={4}
            placeholder={defaultDescription}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            required
          />

          <Divider />
          
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <PhotoCameraIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                {t('labels.attachments', 'Pièces jointes (optionnel)')}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              {t('helper.attachments', 'Ajoutez des photos pour appuyer votre demande (dommages, captures d\'écran, etc.)')}
            </Typography>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              disabled={submitting || uploading}
              fullWidth
            >
              {t('actions.addPhotos', 'Ajouter des photos')}
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            
            {files.length > 0 && (
              <Box mt={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {files.map((fileObj, index) => (
                    <Chip
                      key={index}
                      label={fileObj.name}
                      onDelete={() => handleRemoveFile(index)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      avatar={
                        <Box
                          component="img"
                          src={fileObj.preview}
                          alt={fileObj.name}
                          sx={{ width: 24, height: 24, objectFit: 'cover', borderRadius: '50%' }}
                        />
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {actorRole === 'owner' && (
            <>
              <Divider />
              <FormControlLabel
                control={
                  <Switch
                    checked={autoHoldDeposit}
                    onChange={(event) => setAutoHoldDeposit(event.target.checked)}
                    color="primary"
                  />
                }
                label={t('labels.autoHold', 'Bloquer automatiquement le dépôt de garantie')}
              />
              <Typography variant="caption" color="text.secondary">
                {t('helper.deposit', 'Nous vérifierons si le dépôt peut être retenu en attendant la résolution.')} 
              </Typography>
            </>
          )}

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || uploading || checkingDispute || !!existingDispute}
        >
          {uploading
            ? t('actions.uploading', 'Téléchargement...')
            : submitting
            ? t('actions.submitting', 'Envoi en cours...')
            : checkingDispute
            ? t('actions.checking', 'Vérification...')
            : existingDispute
            ? t('actions.disabledExisting', 'Litige existant')
            : t('actions.submit', 'Lancer la dispute')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisputeCreationModal;
